import React, { useState, useEffect, useContext, useMemo, useRef } from "react";
import TextField from "@mui/material/TextField";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Popover from "@mui/material/Popover";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import IconButton from "@mui/material/IconButton";
import PrintIcon from "@mui/icons-material/Print";
import SettingsIcon from "@mui/icons-material/Settings";
import axios from "axios";
import { useHistory, useParams } from "react-router-dom";
import { ApiLink } from "../../APILink/ApiLink";
import authHeader from "../../Auth/authHeader";
import LoginUserDetailed from "../../Auth/LoginUserDetailed";
import { errorAlert, handleSuccess } from "../../Components/Alerts";
import { FiscalYearContext } from "../../FiscalYear/FiscalYearContext";
import { getUnitID } from "../../Auth/GetUnitCompany";
import { useVatDeclarant } from "../../GetData/Context/CompanyDataContext";
import AccountsAutocompleteEdit from "../../Components/AccountsAutocompleteEdit";
import AccountsAutocompleteInsert from "../../Components/AccountsAutocompleteInsert";
import PriceField from "../../Components/NumberFieldFormater";
import PaymentTypeSelect from "../../Components/PaymentTypeSelect";
import ClientsModal from "../ClientsCRUD/ClientsModal";
import PrintCreditNote from "../../Print/PrintCreditNote";
import { getAccountsDataByID } from "../../GetData/GetSuppliersData";
import { getAllMyCompanyData, getMyCompanyBanks } from "../../GetData/GetCompanyData";
import "./DebitNoteForm.scss";

const formatEuroValue = (value) =>
  Number(value || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const round2 = (value) => {
  const parsed = Number(value || 0);
  if (!Number.isFinite(parsed)) {
    return 0;
  }
  return Math.round((parsed + Number.EPSILON) * 100) / 100;
};

const round4 = (value) => {
  const parsed = Number(value || 0);
  if (!Number.isFinite(parsed)) {
    return 0;
  }
  return Math.round((parsed + Number.EPSILON) * 10000) / 10000;
};

const createEmptySnapshot = () => ({
  invoiceNR: "",
  date: "",
  payDate: "",
  clientID: "",
  paymentOptions: "",
  transactionType: 1,
  coment: "",
  amount0: 0,
  amount8: 0,
  amount18: 0,
  vat8: 0,
  vat18: 0,
  valueWithVAT0: 0,
  valueWithVAT8: 0,
  valueWithVAT18: 0,
});

const createSnapshotWithInvoice = (invoiceValue = "") => ({
  ...createEmptySnapshot(),
  invoiceNR: String(invoiceValue || ""),
});

const SUGGESTIONS = [
  { id: 1, name: "Rabat vjetor sipas marveshjes 18% TVSH" },
  { id: 2, name: "Rabat vjetor sipas marveshjes 8% TVSH" },
  { id: 3, name: "Rabat vjetor sipas marveshjes 8% dhe 18% TVSH" },
  { id: 4, name: "Notë Debitore/Kreditore 18% TVSH" },
  { id: 5, name: "Notë Debitore/Kreditore 8% TVSH" },
  { id: 6, name: "Notë Debitore/Kreditore 8% dhe 18% TVSH" },
];

const TRANSACTION_DATA = [
  { id: 1, type: "Vendore" },
  { id: 2, type: "Eksport" },
];

const formatInvoiceDisplay = (fiscalYear, invoiceValue) => {
  if (invoiceValue == null || invoiceValue === "") {
    return "";
  }

  const normalizedInvoice = String(invoiceValue);
  return normalizedInvoice.startsWith(`DNT-${fiscalYear}-`)
    ? normalizedInvoice
    : `DNT-${fiscalYear}-${normalizedInvoice}`;
};

export default function DebitNoteForm() {
  const history = useHistory();
  const { transactionId } = useParams();
  const isEditMode = Boolean(transactionId);
  const headers = useMemo(() => authHeader(), []);
  const email = LoginUserDetailed();
  const { fiscalYear } = useContext(FiscalYearContext);
  const isVatDeclarant = useVatDeclarant();
  const invoiceNrRef = useRef(null);
  const anchorRef = useRef(null);
  const printSettingsRef = useRef(null);

  const [minDate, setMinDate] = useState("");
  const [maxDate, setMaxDate] = useState("");
  const [date, setDate] = useState("");
  const [payDate, setPayDate] = useState("");
  const [invoiceNR, setInvoiceNR] = useState("");
  const [clientID, setClientID] = useState("");
  const [amount0, setAmount0] = useState(0);
  const [amount8, setAmount8] = useState(0);
  const [amount18, setAmount18] = useState(0);
  const [vat8, setVat8] = useState(0);
  const [vat18, setVat18] = useState(0);
  const [coment, setComent] = useState("");
  const [paymentOptions, setPaymentOptions] = useState("");
  const [transactionType, setTransactionType] = useState(1);
  const [unId, setUnID] = useState("");
  const [editingTransactionId, setEditingTransactionId] = useState("");
  const [field, setField] = useState(null);
  const [valueWithVAT0, setValueWithVAT0] = useState(0);
  const [valueWithVAT8, setValueWithVAT8] = useState(0);
  const [valueWithVAT18, setValueWithVAT18] = useState(0);
  const [showClientModal, setShowClientModal] = useState(false);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [duplicateInvoiceDialog, setDuplicateInvoiceDialog] = useState({
    open: false,
    title: "",
    message: "",
    details: "",
  });
  const [initialFormSnapshot, setInitialFormSnapshot] = useState(createEmptySnapshot());
  const [insertingTransaction, setInsertingTransaction] = useState(false);
  const [isInitializing, setIsInitializing] = useState(isEditMode);
  const [clientDetails, setClientDetails] = useState([]);
  const [companiesData, setCompaniesData] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [hasElectronicSeal, setHasElectronicSeal] = useState(null);
  const [openPrintOptions, setOpenPrintOptions] = useState(false);
  const [validationErrors, setValidationErrors] = useState({
    invoiceNR: "",
    date: "",
    clientID: "",
    paymentOptions: "",
    coment: "",
  });

  const paymentMenuProps = {
    PaperProps: {
      sx: {
        maxHeight: 280,
        maxWidth: "min(460px, calc(100vw - 32px))",
        mt: 0.5,
        overflowY: "auto",
        borderRadius: "10px",
        "& .MuiMenuItem-root": {
          fontSize: "13px",
          minHeight: 36,
          whiteSpace: "normal",
          wordBreak: "break-word",
        },
      },
    },
  };

  const sharedDropdownIcon = (props) => (
    <svg
      {...props}
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="#4E7B67"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );

  const sharedSelectInputSx = {
    borderRadius: 8,
    height: 48,
    minHeight: 48,
    maxHeight: 48,
    background: "#f5f9f7",
    borderColor: "#d4e2db",
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "#4E7B67",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "#4E7B67",
    },
    "& .MuiSelect-select, & .MuiInputBase-input": {
      height: 48,
      minHeight: 48,
      maxHeight: 48,
      boxSizing: "border-box",
      display: "flex",
      alignItems: "center",
      padding: "0 14px",
    },
  };

  const sharedSelectFormControlSx = {
    borderRadius: 8,
    height: 48,
    minHeight: 48,
    maxHeight: 48,
  };

  const descriptionOptions = useMemo(() => {
    const trimmedComment = String(coment || "").trim();

    if (!trimmedComment) {
      return SUGGESTIONS;
    }

    const optionExists = SUGGESTIONS.some((item) => item.name === trimmedComment);

    return optionExists
      ? SUGGESTIONS
      : [{ id: 0, name: trimmedComment }, ...SUGGESTIONS];
  }, [coment]);

  useEffect(() => {
    setMinDate(`${fiscalYear}-01-01`);
    setMaxDate(`${fiscalYear}-12-31`);
  }, [fiscalYear]);

  const printData = {
    InvoiceNr: invoiceNR,
    Date: date,
    Amount0: amount0,
    Amount8: amount8,
    Amount18: amount18,
    VAT8: vat8,
    VAT18: vat18,
    PaymentOptions: paymentOptions,
    TransactionType: transactionType,
    Coment: coment,
    ModifiedByUser: email,
  };

  const currentFormSnapshot = useMemo(
    () => ({
      invoiceNR: invoiceNR || "",
      date: date || "",
      payDate: payDate || "",
      clientID: String(clientID || ""),
      paymentOptions: String(paymentOptions || ""),
      transactionType: Number(transactionType || 1),
      coment: coment || "",
      amount0: round4(amount0),
      amount8: round4(amount8),
      amount18: round4(amount18),
      vat8: round4(vat8),
      vat18: round4(vat18),
      valueWithVAT0: round2(valueWithVAT0),
      valueWithVAT8: round2(valueWithVAT8),
      valueWithVAT18: round2(valueWithVAT18),
    }),
    [
      invoiceNR,
      date,
      payDate,
      clientID,
      paymentOptions,
      transactionType,
      coment,
      amount0,
      amount8,
      amount18,
      vat8,
      vat18,
      valueWithVAT0,
      valueWithVAT8,
      valueWithVAT18,
    ],
  );

  const isFormDirty = useMemo(
    () => JSON.stringify(currentFormSnapshot) !== JSON.stringify(initialFormSnapshot),
    [currentFormSnapshot, initialFormSnapshot],
  );

  const closePage = () => {
    history.push("/Sales/debitnote");
  };

  const closeDuplicateInvoiceDialog = () => {
    setDuplicateInvoiceDialog({
      open: false,
      title: "",
      message: "",
      details: "",
    });
  };

  const handleCloseClick = () => {
    if (isFormDirty) {
      setShowCloseConfirm(true);
      return;
    }
    closePage();
  };

  const handleTogglePrintOptions = (event) => {
    setOpenPrintOptions((prev) => !prev);
  };

  const handleClosePrintOptions = () => {
    setOpenPrintOptions(false);
  };

  const fetchClientDetails = async (accountId) => {
    if (!accountId) {
      setClientDetails([]);
      return;
    }

    try {
      const data = await getAccountsDataByID(accountId);
      setClientDetails(data);
    } catch (err) {
      errorAlert(err.message);
    }
  };

  const fetchCompaniesData = async () => {
    try {
      const data = await getAllMyCompanyData();
      setCompaniesData(data);
      setHasElectronicSeal(data[0]?.ElectronicSeal ?? false);
    } catch (err) {
      errorAlert(err.message);
    }
  };

  const fetchBanksAccount = async () => {
    try {
      const data = await getMyCompanyBanks();
      setBankAccounts(data);
    } catch (err) {
      errorAlert(err.message);
    }
  };

  const resetCreateForm = (nextInvoiceNr = "") => {
    const displayInvoice = formatInvoiceDisplay(fiscalYear, nextInvoiceNr);
    setDate("");
    setPayDate("");
    setInvoiceNR(displayInvoice);
    setClientID("");
    setField(null);
    setComent("");
    setPaymentOptions("");
    setAmount0(0);
    setAmount8(0);
    setAmount18(0);
    setTransactionType(1);
    setValueWithVAT0(0);
    setValueWithVAT8(0);
    setValueWithVAT18(0);
    setVat8(0);
    setVat18(0);
    setInitialFormSnapshot(createSnapshotWithInvoice(displayInvoice));
  };

  const fetchNextInvoiceNr = async ({ resetForm = false } = {}) => {
    try {
      const response = await axios.get(ApiLink.API_URL + "DebitNote/GetNextInvoiceNR", {
        headers,
        params: { fiscalYear },
      });

      const nextInvoice = response?.data?.NextInvoiceNR;
      if (nextInvoice != null) {
        const normalizedInvoice = String(nextInvoice);
        const displayInvoice = formatInvoiceDisplay(fiscalYear, normalizedInvoice);

        if (resetForm) {
          resetCreateForm(normalizedInvoice);
        } else {
          setInvoiceNR(displayInvoice);
          setInitialFormSnapshot(createSnapshotWithInvoice(displayInvoice));
        }

        return normalizedInvoice;
      }
    } catch (error) {
      // preserve current invoice on failure
    }

    return "";
  };

  const validateBeforeSubmit = () => {
    const errors = {
      invoiceNR: "",
      date: "",
      clientID: "",
      paymentOptions: "",
      coment: "",
    };

    if (!invoiceNR) {
      errors.invoiceNR = "Numri i faturës është i detyrueshëm.";
    }
    if (!date) {
      errors.date = "Data e faturës është e detyrueshme.";
    }
    if (!clientID) {
      errors.clientID = "Ju lutem zgjidhni klientin.";
    }
    if (!paymentOptions) {
      errors.paymentOptions = "Ju lutem zgjidhni mënyrën e pagesës.";
    }
    if (!coment) {
      errors.coment = "Ju lutem zgjidhni përshkrimin.";
    }

    setValidationErrors(errors);
    return !Object.values(errors).some((error) => error !== "");
  };

  const calculateWithoutVAT = async (withVAT, vat) => {
    try {
      const noVAT = (Number(withVAT) * Number(100)) / (Number(100) + Number(vat));
      const vatTotal = Number(withVAT) - Number(noVAT);

      if (vat == 0) {
        setAmount0(round4(noVAT));
      } else if (vat == 8) {
        setAmount8(round4(noVAT));
        setVat8(round4(vatTotal));
      } else if (vat == 18) {
        setAmount18(round4(noVAT));
        setVat18(round4(vatTotal));
      }
    } catch (error) {
      console.error("Error during calculation:", error);
    }
  };

  const calculateWithVAT = async (withoutVAT, vat) => {
    try {
      const baseAmount = Number(withoutVAT || 0);
      const vatTotal = baseAmount * (Number(vat) / Number(100));
      const withVAT = baseAmount + Number(vatTotal);

      if (vat == 0) {
        setAmount0(round4(baseAmount));
        setValueWithVAT0(round2(withVAT));
      } else if (vat == 8) {
        setAmount8(round4(baseAmount));
        setValueWithVAT8(round2(withVAT));
        setVat8(round4(vatTotal));
      } else if (vat == 18) {
        setAmount18(round4(baseAmount));
        setValueWithVAT18(round2(withVAT));
        setVat18(round4(vatTotal));
      }
    } catch (error) {
      console.error("Error during calculation:", error);
    }
  };

  const calculateFromVAT = async (vatValue, vat) => {
    try {
      const withoutVAT = Number(vatValue) / (Number(vat) / Number(100));
      const totalAmount = Number(withoutVAT) + Number(vatValue);

      if (vat == 0) {
        setValueWithVAT0(round2(totalAmount));
        setAmount0(round4(withoutVAT));
      } else if (vat == 8) {
        setValueWithVAT8(round2(totalAmount));
        setAmount8(round4(withoutVAT));
      } else if (vat == 18) {
        setValueWithVAT18(round2(totalAmount));
        setAmount18(round4(withoutVAT));
      }
    } catch (error) {
      console.error("Error during calculation:", error);
    }
  };

  useEffect(() => {
    getUnitID(headers).then(setUnID);
  }, [headers]);

  useEffect(() => {
    fetchCompaniesData();
    fetchBanksAccount();
  }, []);

  useEffect(() => {
    fetchClientDetails(clientID);
  }, [clientID]);

  useEffect(() => {
    if (!isEditMode) {
      fetchNextInvoiceNr({ resetForm: true }).then(() => {
        invoiceNrRef.current?.focus();
      });
      return;
    }

    setIsInitializing(true);
    let isCancelled = false;

    const initEditMode = async () => {
      try {
        const response = await axios.get(
          ApiLink.API_URL + `DebitNote/view-mode/${transactionId}`,
          { headers },
        );

        if (isCancelled) {
          return;
        }

        const item = response.data || {};
        const invoiceDateIso = (item?.Date || "").substring(0, 10);
        const paymentDateIso = (item?.PayDate || item?.Date || "").substring(0, 10);
        const amount0Value = Number(item?.Amount0 || 0);
        const amount8Value = Number(item?.Amount8 || 0);
        const amount18Value = Number(item?.Amount18 || 0);
        const vat8Value = Number(item?.VAT8 || 0);
        const vat18Value = Number(item?.VAT18 || 0);
        const clientAccountId = item?.ClientID || item?.AccountID || "";

        setEditingTransactionId(item?.TransactionID || transactionId || "");
        setInvoiceNR(formatInvoiceDisplay(fiscalYear, item?.InvoiceNr));
        setDate(invoiceDateIso);
        setPayDate(paymentDateIso);
        setClientID(String(clientAccountId || ""));
        setPaymentOptions(String(item?.PaymentOptions ?? ""));
        setTransactionType(Number(item?.TransactionType || 1));
        setComent(item?.Coment || "");
        setAmount0(round4(amount0Value));
        setAmount8(round4(amount8Value));
        setAmount18(round4(amount18Value));
        setVat8(round4(vat8Value));
        setVat18(round4(vat18Value));
        setValueWithVAT0(round2(amount0Value));
        setValueWithVAT8(round2(amount8Value + vat8Value));
        setValueWithVAT18(round2(amount18Value + vat18Value));
        setField(
          clientAccountId
            ? {
                AccountID: clientAccountId,
                AccountName: item?.AccountName || "",
              }
            : null,
        );

        setInitialFormSnapshot({
          invoiceNR: formatInvoiceDisplay(fiscalYear, item?.InvoiceNr),
          date: invoiceDateIso,
          payDate: paymentDateIso,
          clientID: String(clientAccountId || ""),
          paymentOptions: String(item?.PaymentOptions ?? ""),
          transactionType: Number(item?.TransactionType || 1),
          coment: item?.Coment || "",
          amount0: round4(amount0Value),
          amount8: round4(amount8Value),
          amount18: round4(amount18Value),
          vat8: round4(vat8Value),
          vat18: round4(vat18Value),
          valueWithVAT0: round2(amount0Value),
          valueWithVAT8: round2(amount8Value + vat8Value),
          valueWithVAT18: round2(amount18Value + vat18Value),
        });
      } catch (error) {
        if (!isCancelled) {
          errorAlert("Gabim në marrjen e të dhënave për editim.");
          history.push("/Sales/debitnote");
        }
      } finally {
        if (!isCancelled) {
          setIsInitializing(false);
        }
      }
    };

    initEditMode();

    return () => {
      isCancelled = true;
    };
  }, [isEditMode, headers, transactionId, history, fiscalYear]);

  useEffect(() => {
    if (!isInitializing) {
      invoiceNrRef.current?.focus();
    }
  }, [isInitializing]);

  const handleSubmit = (event) => {
    event.preventDefault();

    if (!validateBeforeSubmit()) {
      setInsertingTransaction(false);
      return;
    }

    setInsertingTransaction(true);

    const numericInvoice = String(invoiceNR || "").startsWith(`DNT-${fiscalYear}-`)
      ? String(invoiceNR).replace(`DNT-${fiscalYear}-`, "")
      : invoiceNR;

    const stateDate = {
      Date: date,
      ActionType: "DNT",
      InvoiceNr: numericInvoice,
      SupplierID: Number(clientID || 0),
      Amount0: Number(amount0 || 0),
      Amount8: Number(amount8 || 0),
      Amount18: Number(amount18 || 0),
      PaymentOptions: Number(paymentOptions || 0),
      TransactionType: Number(transactionType || 1),
      CompanyUnitID: unId > 0 ? unId : null,
      Coment: coment,
      ...(isEditMode
        ? {
            TransactionID: Number(editingTransactionId || transactionId),
            ModifiedByUser: email,
          }
        : {
            CreatedByUser: email,
          }),
    };

    const request = isEditMode
      ? axios.put(
          ApiLink.API_URL + "DebitNote/update-debitnote",
          {
            ...stateDate,
            AccountID: Number(clientID),
            AccountIDSpare: 702,
          },
          { headers },
        )
      : axios.post(ApiLink.API_URL + "DebitNote/insert-debitnote", stateDate, { headers });

    request
      .then(() => {
        handleSuccess(
          isEditMode ? "Debit nota u përditësua me sukses" : "Debit nota u shtua me sukses!",
        );

        if (isEditMode) {
          closePage();
          return;
        }

        fetchNextInvoiceNr({ resetForm: true }).then((nextInvoice) => {
          setInvoiceNR(formatInvoiceDisplay(fiscalYear, nextInvoice));
          setInitialFormSnapshot(
            createSnapshotWithInvoice(formatInvoiceDisplay(fiscalYear, nextInvoice)),
          );
        });
        setInsertingTransaction(false);
      })
      .catch((error) => {
        setInsertingTransaction(false);
        if (error.response && error.response.status === 409) {
          const backendMessage =
            typeof error.response.data === "string" && error.response.data.trim()
              ? error.response.data.trim()
              : "Ky numër i faturës ekziston tashmë për këtë vit fiskal.";

          if (!isEditMode) {
            fetchNextInvoiceNr({ resetForm: false }).then((nextInvoice) => {
              if (nextInvoice) {
                setInvoiceNR(formatInvoiceDisplay(fiscalYear, nextInvoice));
              }
            });
          }

          setDuplicateInvoiceDialog({
            open: true,
            title: !isEditMode ? "Numri i faturës u rifreskua" : "Numri i faturës ekziston",
            message: backendMessage,
            details: !isEditMode
              ? "Sistemi gjeneroi automatikisht një numër të ri të faturës. Të dhënat e formularit mbetën të pandryshuara dhe mund ta vazhdoni ruajtjen menjëherë."
              : "Ju lutem verifikoni numrin e faturës dhe provoni përsëri pasi të bëni korrigjimin e nevojshëm.",
          });
        } else {
          errorAlert(
            isEditMode
              ? "Ndodhi gabim gjatë përditësimit të debit notes."
              : "Ndodhi gabim gjatë ruajtjes së debit notes.",
          );
        }
      });
  };

  if (isInitializing) {
    return (
      <div className="Unit adv-form-page" style={{ minHeight: "280px" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
            minHeight: "280px",
          }}
        >
          <CircularProgress size={28} />
        </div>
      </div>
    );
  }

  return (
    <div className="Unit adv-form-page">
      <div className="adv-form-header">
        <div>
          <h2 className="adv-form-title">
            {isEditMode ? "Ndrysho debit noten" : "Debit note e re"}
          </h2>
          <p className="adv-form-subtitle">
            {isEditMode
              ? "Përditëso fushat e debit notes dhe ruaj ndryshimet pa prekur logjikën ekzistuese të kalkulimeve."
              : "Plotëso fushat kryesore, gjenero faturën nga databaza dhe ruaj debit noten."}
          </p>
        </div>
        <div className="adv-form-header-actions">
          {!isEditMode && (
            <button
              type="button"
              className="adv-btn adv-btn-secondary"
              onClick={() =>
                fetchNextInvoiceNr({ resetForm: true }).then((nextInvoice) => {
                  setInvoiceNR(formatInvoiceDisplay(fiscalYear, nextInvoice));
                  setInitialFormSnapshot(
                    createSnapshotWithInvoice(formatInvoiceDisplay(fiscalYear, nextInvoice)),
                  );
                })
              }
              disabled={insertingTransaction}
            >
              Pastro
            </button>
          )}
          <div style={{ display: "none" }} ref={anchorRef}>
            <PrintCreditNote
              data={printData}
              clientData={clientDetails}
              companyData={companiesData}
              formType={"debi"}
              hasElectronicSeal={hasElectronicSeal}
              bankaccounts={bankAccounts}
            />
          </div>
          <button
            type="button"
            className="advform-printbtn-main"
            onClick={() => anchorRef.current?.querySelector(".button-15")?.click()}
          >
            <PrintIcon style={{ fontSize: 16, marginRight: 6 }} />
            Print
          </button>
          <button
            ref={printSettingsRef}
            type="button"
            className="advform-printbtn-settings"
            onClick={handleTogglePrintOptions}
            aria-label="Opsionet e printimit"
            title="Opsionet e printimit"
          >
            <SettingsIcon style={{ fontSize: 16 }} />
          </button>
          <Popover
            open={openPrintOptions}
            anchorEl={printSettingsRef.current}
            onClose={handleClosePrintOptions}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            PaperProps={{
              sx: {
                borderRadius: "12px",
                border: "1px solid #daeae3",
                overflow: "hidden",
                minWidth: 220,
                mt: 0.5,
              },
            }}
          >
            <div className="adv-print-menu">
              <div className="adv-print-menu-header">Opsionet e Printimit</div>
              <div className="adv-print-menu-divider" />
              <FormControlLabel
                className="adv-print-menu-item"
                control={
                  <Checkbox
                    checked={Boolean(hasElectronicSeal)}
                    onChange={(event) => setHasElectronicSeal(event.target.checked)}
                    size="small"
                    sx={{
                      color: "#9eb8ab",
                      "&.Mui-checked": { color: "#4E7B67" },
                    }}
                  />
                }
                label={<span className="adv-print-menu-label">Vula Elektronike</span>}
              />
            </div>
          </Popover>

          <button
            type="button"
            className="adv-btn adv-btn-ghost"
            onClick={handleCloseClick}
            disabled={insertingTransaction}
          >
            Mbyll
          </button>
          <button
            type="submit"
            form="advance-form"
            className="adv-btn adv-btn-primary"
            disabled={insertingTransaction || !isFormDirty}
          >
            {insertingTransaction ? (
              <CircularProgress size={18} style={{ color: "#fff" }} />
            ) : isEditMode ? (
              "Përditëso"
            ) : (
              "Ruaj"
            )}
          </button>
        </div>
      </div>

      <form id="advance-form" className="adv-form-body" onSubmit={handleSubmit} noValidate>
        <div className="adv-top-grid">
          <section className="adv-card adv-card-elevated">
            <div className="adv-card-headline">
              <h3 className="adv-card-title">Informacioni i Përgjithshëm</h3>
              <span className="adv-card-chip">Dokumenti</span>
            </div>
            <div className="adv-form-cluster">
              <p className="adv-cluster-title">Detajet e Dokumentit</p>
              <div className="adv-grid two-columns">
                <TextField
                  label="Numri Fatures"
                  fullWidth
                  required
                  inputRef={invoiceNrRef}
                  value={invoiceNR}
                  InputProps={{ readOnly: true }}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  label="Data Faturës"
                  type="date"
                  value={date}
                  onChange={(e) => {
                    setDate(e.target.value);
                    setPayDate(e.target.value);
                    setValidationErrors({ ...validationErrors, date: "" });
                  }}
                  required
                  error={!!validationErrors.date}
                  helperText={validationErrors.date}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: minDate, max: maxDate }}
                />
                <div className="adv-grid-full">
                  <div className="adv-client-row">
                    <div style={{ flex: 1 }}>
                      {isEditMode ? (
                        <AccountsAutocompleteEdit
                          ledgerID={105}
                          textFieldWidth="100%"
                          accountID={clientID}
                          label="Kerko Klientin"
                          labelId="AccountID"
                          onSelect={(account) => {
                            setClientID(account.AccountID);
                            setField(account);
                            setValidationErrors({ ...validationErrors, clientID: "" });
                          }}
                          onClear={() => {
                            setClientID("");
                            setField(null);
                          }}
                          error={!!validationErrors.clientID}
                          helperText={validationErrors.clientID}
                        />
                      ) : (
                        <AccountsAutocompleteInsert
                          ledgerID={105}
                          textFieldWidth="100%"
                          value={field}
                          label="Kerko Klientin"
                          labelId="AccountID"
                          onSelect={(account) => {
                            setClientID(account.AccountID);
                            setField(account);
                            setValidationErrors({ ...validationErrors, clientID: "" });
                          }}
                          onClear={() => {
                            setClientID("");
                            setField(null);
                          }}
                          error={!!validationErrors.clientID}
                          helperText={validationErrors.clientID}
                        />
                      )}
                    </div>
                    <IconButton
                      size="small"
                      onClick={() => setShowClientModal(true)}
                      aria-label="Shto klient"
                      title="Shto klient"
                      sx={{
                        border: "1px solid #c5d8ce",
                        borderRadius: "8px",
                        color: "#2f5649",
                        fontSize: "22px",
                        fontWeight: 700,
                        width: 48,
                        height: 48,
                      }}
                    >
                      +
                    </IconButton>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="adv-card adv-card-elevated">
            <div className="adv-card-headline">
              <h3 className="adv-card-title">Klasifikimi</h3>
              <span className="adv-card-chip">Kontabiliteti</span>
            </div>
            <div className="adv-form-cluster">
              <p className="adv-cluster-title">Kategorizimi i Debit notat</p>
              <div className="adv-grid two-columns">
                <div className="adv-grid-full">
                  <TextField
                    label="Llogaria Kontabël"
                    value="702-Te hyrat e Tjera"
                    disabled
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                  />
                </div>
                <TextField
                  select
                  label="Tipi i Transaksionit"
                  value={transactionType}
                  onChange={(event) => setTransactionType(event.target.value)}
                  required
                  SelectProps={{
                    MenuProps: paymentMenuProps,
                    IconComponent: sharedDropdownIcon,
                  }}
                  InputProps={{
                    sx: sharedSelectInputSx,
                  }}
                  FormControlProps={{
                    sx: sharedSelectFormControlSx,
                  }}
                  InputLabelProps={{ shrink: true }}
                >
                  {TRANSACTION_DATA.map((data) => (
                    <MenuItem key={data.id} value={data.id}>
                      {data.type}
                    </MenuItem>
                  ))}
                </TextField>
                <PaymentTypeSelect
                  value={paymentOptions}
                  onChange={(event) => {
                    setPaymentOptions(event.target.value);
                    setValidationErrors({ ...validationErrors, paymentOptions: "" });
                  }}
                  label="Mënyra Pagesës"
                  error={!!validationErrors.paymentOptions}
                  helperText={validationErrors.paymentOptions}
                />
                <div className="adv-grid-full">
                  <TextField
                    className="adv-comment"
                    label="Përshkrimi"
                    select
                    required
                    value={coment}
                    onChange={(event) => {
                      setComent(event.target.value);
                      setValidationErrors({ ...validationErrors, coment: "" });
                    }}
                    fullWidth
                    error={!!validationErrors.coment}
                    helperText={validationErrors.coment}
                    SelectProps={{
                      MenuProps: paymentMenuProps,
                      IconComponent: sharedDropdownIcon,
                    }}
                    InputProps={{
                      sx: sharedSelectInputSx,
                    }}
                    FormControlProps={{
                      sx: sharedSelectFormControlSx,
                    }}
                    InputLabelProps={{ shrink: true }}
                  >
                    {descriptionOptions.map((item) => (
                      <MenuItem key={item.id} value={item.name}>
                        {item.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </div>
              </div>
            </div>
          </section>
        </div>

        <section className="adv-card">
          <h3 className="adv-card-title">Vlerat dhe TVSH</h3>
          <div className="adv-vat-table">
            <div className="adv-vat-head">
              <div>Norma TVSH</div>
              <div>Vlera pa TVSH</div>
              <div>Vlera e TVSH</div>
              <div>Total</div>
            </div>

            {isVatDeclarant && (
              <>
                <div className="adv-vat-row">
                  <div className="adv-vat-rate">18%</div>
                  <PriceField
                    label=""
                    name="amount18"
                    value={amount18}
                    onChange={(event) => calculateWithVAT(event.target.value, 18)}
                  />
                  <PriceField
                    label=""
                    name="vat18"
                    value={vat18}
                    onChange={(event) => calculateFromVAT(event.target.value, 18)}
                  />
                  <PriceField
                    label=""
                    name="total18"
                    value={valueWithVAT18}
                    onChange={(event) => calculateWithoutVAT(event.target.value, 18)}
                    decimalScale={2}
                  />
                </div>

                <div className="adv-vat-row">
                  <div className="adv-vat-rate">8%</div>
                  <PriceField
                    label=""
                    name="amount8"
                    value={amount8}
                    onChange={(event) => calculateWithVAT(event.target.value, 8)}
                  />
                  <PriceField
                    label=""
                    name="vat8"
                    value={vat8}
                    onChange={(event) => calculateFromVAT(event.target.value, 8)}
                  />
                  <PriceField
                    label=""
                    name="total8"
                    value={valueWithVAT8}
                    onChange={(event) => calculateWithoutVAT(event.target.value, 8)}
                    decimalScale={2}
                  />
                </div>
              </>
            )}

            <div className="adv-vat-row">
              <div className="adv-vat-rate">0%</div>
              <PriceField
                label=""
                name="amount0"
                value={amount0}
                onChange={(event) => calculateWithVAT(event.target.value, 0)}
              />
              <PriceField label="" name="vat0" value={0} disabled />
              <PriceField
                label=""
                name="total0"
                value={valueWithVAT0}
                onChange={(event) => calculateWithoutVAT(event.target.value, 0)}
                decimalScale={2}
              />
            </div>
          </div>

          <div className="adv-vat-totals-row">
            <div className="adv-vat-totals-label">Totale</div>
            <div className="adv-vat-total-item">
              <span>Total pa TVSH</span>
              <strong>{formatEuroValue(Number(amount0) + Number(amount8) + Number(amount18))} €</strong>
            </div>
            <div className="adv-vat-total-item">
              <span>Total TVSH</span>
              <strong>{formatEuroValue(Number(vat8) + Number(vat18))} €</strong>
            </div>
            <div className="adv-vat-total-item grand-total">
              <span>Total me TVSH</span>
              <strong>
                {formatEuroValue(
                  Number(amount0) +
                    Number(amount8) +
                    Number(amount18) +
                    Number(vat8) +
                    Number(vat18),
                )}{" "}
                €
              </strong>
            </div>
          </div>
        </section>
      </form>

      <ClientsModal
        show={showClientModal}
        onHide={() => setShowClientModal(false)}
        mode="insert"
        type="none"
      />

      <Dialog open={showCloseConfirm} onClose={() => setShowCloseConfirm(false)}>
        <DialogTitle>Konfirmim për mbyllje</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Keni ndryshime të paruajtura. Nëse vazhdoni, ato do të humbin.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCloseConfirm(false)}>Qëndro</Button>
          <Button
            color="error"
            onClick={() => {
              setShowCloseConfirm(false);
              closePage();
            }}
          >
            Mbyll pa ruajtur
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={duplicateInvoiceDialog.open} onClose={closeDuplicateInvoiceDialog}>
        <DialogTitle>{duplicateInvoiceDialog.title}</DialogTitle>
        <DialogContent>
          <DialogContentText>{duplicateInvoiceDialog.message}</DialogContentText>
          {duplicateInvoiceDialog.details ? (
            <DialogContentText sx={{ mt: 1.5 }}>
              {duplicateInvoiceDialog.details}
            </DialogContentText>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDuplicateInvoiceDialog} autoFocus>
            Në rregull
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}